'use client';

import * as React from 'react';
import { X } from 'lucide-react';
import { Badge } from './badge';
import { Input } from './input';
import { Command, CommandGroup, CommandItem, CommandList } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/lib/utils';

export interface TagInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  suggestions?: string[] | undefined;
  onQueryChange?: (query: string) => void;
  disabled?: boolean;
  className?: string;
}

export function TagInput({
  value,
  onChange,
  placeholder = 'Add tag...',
  suggestions = [],
  onQueryChange,
  disabled = false,
  className,
}: TagInputProps) {
  const [inputValue, setInputValue] = React.useState('');
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const shouldShowSuggestions = isPopoverOpen && suggestions.length > 0 && inputValue.length >= 3;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Trigger query change for autocomplete after 3 characters
    if (newValue.length >= 3 && onQueryChange) {
      onQueryChange(newValue);
      setIsPopoverOpen(true);
    } else {
      setIsPopoverOpen(false);
    }
  };

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();

    // Don't add empty or duplicate tags
    if (!trimmedTag || value.includes(trimmedTag)) {
      return;
    }

    onChange([...value, trimmedTag]);
    setInputValue('');
    setIsPopoverOpen(false);
    inputRef.current?.focus();
  };

  const removeTag = (indexToRemove: number) => {
    onChange(value.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      // Remove last tag on backspace when input is empty
      removeTag(value.length - 1);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap gap-2 min-h-[2.5rem] p-2 rounded-md border border-input bg-transparent">
        {value.map((tag, index) => (
          <Badge key={`${tag}-${index}`} variant="secondary" className="gap-1">
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="ml-1 rounded-full hover:bg-secondary-foreground/20"
                aria-label={`Remove ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}

        <Popover open={shouldShowSuggestions} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              className="flex-1 min-w-[120px] border-0 shadow-none focus-visible:ring-0 px-0"
            />
          </PopoverTrigger>

          {shouldShowSuggestions && (
            <PopoverContent
              className="p-0"
              align="start"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <Command>
                <CommandList>
                  <CommandGroup>
                    {suggestions.map((suggestion) => (
                      <CommandItem
                        key={suggestion}
                        onSelect={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          )}
        </Popover>
      </div>
    </div>
  );
}
